import type {
  ReferralCodeResponseDto,
  ReferralSummaryResponseDto,
  RedeemResultDto,
} from '@nomiss/sdk';
import {
  RedeemResultDtoRefereeOutcomeEnum,
  RedeemResultDtoReferrerOutcomeEnum,
  ResponseError,
} from '@nomiss/sdk';
import { ApiRequestError, unwrapApiData } from './api-request.util';
import { referralsApi } from './sdk-client';

export type {
  ReferralCodeResponseDto,
  ReferralSummaryResponseDto,
  RedeemResultDto,
};
export {
  RedeemResultDtoRefereeOutcomeEnum,
  RedeemResultDtoReferrerOutcomeEnum,
};

/** Coerced redeem result — SDK types expiresAt as `object | null`. */
export type RedeemReferralResult = {
  redeemed: boolean;
  refereeOutcome: RedeemResultDto['refereeOutcome'];
  refereeExpiresAt: string | null;
  referrerOutcome: RedeemResultDto['referrerOutcome'];
  referrerExpiresAt: string | null;
};

export class ReferralError extends ApiRequestError {
  constructor(
    status: number,
    message: string,
    readonly errorCode: string | null = null,
  ) {
    super(status, message, 'ReferralError');
  }
}

async function parseReferralError(
  response: Response,
  fallback: string,
): Promise<ReferralError> {
  const json = (await response.json().catch(() => ({}))) as {
    message?: string;
    errorCode?: string | null;
    code?: string;
  };

  const errorCode =
    (typeof json.errorCode === 'string' ? json.errorCode : null) ??
    (typeof json.code === 'string' ? json.code : null) ??
    (response.status === 409 ? 'ALREADY_REDEEMED' : null);

  return new ReferralError(
    response.status,
    typeof json.message === 'string' ? json.message : fallback,
    errorCode,
  );
}

async function executeReferralRequest<T>(options: {
  request: () => Promise<{ raw: Response }>;
  fallback: string;
}): Promise<T> {
  try {
    const raw = await options.request();
    if (!raw.raw.ok) {
      throw await parseReferralError(raw.raw, options.fallback);
    }

    const json = await raw.raw.json();
    return unwrapApiData<T>(json);
  } catch (error) {
    if (error instanceof ReferralError) {
      throw error;
    }

    if (error instanceof ResponseError) {
      throw await parseReferralError(error.response, options.fallback);
    }

    throw error;
  }
}

function coerceExpiresAt(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  return null;
}

function normalizeRedeemResult(data: RedeemResultDto): RedeemReferralResult {
  return {
    redeemed: data.redeemed,
    refereeOutcome: data.refereeOutcome,
    refereeExpiresAt: coerceExpiresAt(data.refereeExpiresAt),
    referrerOutcome: data.referrerOutcome,
    referrerExpiresAt: coerceExpiresAt(data.referrerExpiresAt),
  };
}

export async function getMyReferralCode(): Promise<ReferralCodeResponseDto> {
  return executeReferralRequest<ReferralCodeResponseDto>({
    request: () => referralsApi.referralsControllerMyCodeV1Raw(),
    fallback: 'Failed to load referral code',
  });
}

export async function getMyReferralSummary(): Promise<ReferralSummaryResponseDto> {
  return executeReferralRequest<ReferralSummaryResponseDto>({
    request: () => referralsApi.referralsControllerMySummaryV1Raw(),
    fallback: 'Failed to load referral summary',
  });
}

export async function redeemReferralCode(
  code: string,
): Promise<RedeemReferralResult> {
  const data = await executeReferralRequest<RedeemResultDto>({
    request: () =>
      referralsApi.referralsControllerRedeemV1Raw({
        redeemReferralDto: { code: code.trim().toUpperCase() },
      }),
    fallback: 'Failed to redeem referral code',
  });

  return normalizeRedeemResult(data);
}
